import classNames from 'classnames';
import {
  type FocusEvent,
  type InputHTMLAttributes,
  type MouseEvent,
  forwardRef,
  useState,
} from 'react';

import Icon from 'components/Icon/Icon';
import Tooltip from 'components/Tooltip';

import { useTranslation } from 'utils/translations';
import styles from './Input.module.scss';

type InputProps = InputHTMLAttributes<HTMLInputElement> & {
  isValid?: boolean;
  iconName?: string;
  onIconClick?: (e: MouseEvent<HTMLButtonElement>) => void;
};

const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      value,
      onChange,
      onFocus,
      onBlur,
      className,
      type = 'text',
      name,
      disabled,
      id,
      isValid,
      iconName,
      onIconClick,
      placeholder,
      ...restProps
    },
    ref,
  ) => {
    const [showError, setShowError] = useState<boolean>(false);
    const [focus, setFocus] = useState<boolean>(false);
    const lockedHintText = useTranslation('locked_field_hint');

    const handleFocusEvent = (event: FocusEvent<HTMLInputElement, Element>) => {
      onFocus?.(event);
      setShowError(false);
      setFocus(true);
    };

    const handleBlurEvent = (event: FocusEvent<HTMLInputElement, Element>) => {
      onBlur?.(event);
      setShowError(true);
      setFocus(false);
    };

    const icon = disabled ? 'lock' : iconName;

    return (
      <div className={styles['input-wrapper']}>
        <input
          id={id}
          name={name}
          disabled={disabled}
          type={type}
          value={value}
          onChange={onChange}
          className={classNames(styles.input, className, {
            [styles.error]: !isValid && showError,
            [styles.filled]: isValid && name === 'DOCUMENT_NUMBER',
          })}
          onFocus={handleFocusEvent}
          onBlur={handleBlurEvent}
          ref={ref}
          {...restProps}
        />
        {placeholder && (
          <label
            tabIndex={-1}
            htmlFor={id}
            onClick={() => setFocus(true)}
            onKeyDown={() => setFocus(true)}
            className={classNames(styles.placeholder, {
              [styles.focus]: focus || !!value,
            })}
          >
            {placeholder}
          </label>
        )}
        {icon && (
          <button
            className={styles['input-button']}
            data-testid="input-button"
            type="button"
            tabIndex={-1}
            onClick={onIconClick}
          >
            {disabled ? (
              <Tooltip message={lockedHintText}>
                <Icon name={icon} />
              </Tooltip>
            ) : (
              <Icon name={icon} />
            )}
          </button>
        )}
      </div>
    );
  },
);

export default Input;
